import { useMemo } from 'react';
import { Util } from '../../../util/Util';
import { useUI } from '../../../providers/MoleUIContext';
import { useDailyAggregateData } from '../../../hooks/useDailyAggregateData';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from '../../../util/constants';
import TableView from '../../../components/TableView';
import { MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, Tooltip } from '@mui/material';
import ExportMenuButton from '../../../components/ExportMenuButton';
import { Refresh } from '@mui/icons-material';
import { createSignCell } from '../../../util/cellFormatters';
import { AmountFooter } from '../../../components/DailyAggregateTable';
import { parseISODay } from "../../../util/businessClock";

const SalesItemDaily = () => {
	const { t } = useUI();
	const {
		data,
		lastUpdate,
		dateRange,
		setDateRange,
		loading,
		fetchData,
	} = useDailyAggregateData(LocalStorageKeys.SALES_ITEM_RECORDS, 'sales/items');

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t('common:tables.date'),
			id: 'date',
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), 'medium'),
			filterVariant: 'date-range',
		},
		{
			header: t('common:tables.sector'),
			accessorKey: 'sector',
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:tables.item'),
			accessorKey: 'item_name',
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:tables.pos'),
			accessorKey: 'depot',
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t('common:tables.quantity'),
			accessorKey: 'quantity',
			Cell: createSignCell({ signStyle: 'accounting' }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum',
			AggregatedCell: ({ cell, table }) => (
				<>
					{t('common:tableView.sumBy')}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => (
				<AmountFooter table={table} label={t('common:report.total')} accessorKey="quantity" />
			),
		},
		{
			header: t('common:tables.amount'),
			accessorKey: 'amount',
			Cell: createSignCell({ signStyle: 'accounting' }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t('common:tables.total'),
			accessorKey: 'total',
			Cell: createSignCell({ signStyle: 'accounting' }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum',
			AggregatedCell: ({ cell, table }) => (
				<>
					{t('common:tableView.sumBy')}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => (
				<AmountFooter table={table} label={t('common:report.total')} accessorKey="total" />
			),
		},
	], [t]);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TableView
				stateKey={LocalStorageKeys.SALES_ITEM_RECORDS}
				dataScope="sale"
				header={t('common:sales.headers.byItemDaily')}
				data={data}
				columns={columns}
				includeTimestampColumns={false}
				totals={[
					{ key: 'quantity', label: t('common:tables.quantity') },
					{ key: 'total', label: t('common:tables.total') },
				]}
				lastUpdated={lastUpdate}
				period={dateRange}
				setDateRangeFilter={setDateRange}
				isLoading={loading}
				getRowSx={(row) =>
					Number(row.original.quantity) < 0
						? {
								backgroundColor: (theme: any) =>
									theme.palette.mode === 'dark'
										? 'rgba(255, 167, 38, 0.12)'
										: 'rgba(255, 167, 38, 0.08)',
								borderLeft: '3px solid',
								borderLeftColor: 'warning.main',
							}
						: {}
				}
				enableRowActions={false}
				renderTopToolbarActions={(table) => (
					<Box sx={{ display: 'flex', gap: '0.5rem' }}>
						<Tooltip title={t('common:ui.tooltips.refreshData')}>
							<IconButton onClick={() => void fetchData()} color="primary">
								<Refresh />
							</IconButton>
						</Tooltip>
						<ExportMenuButton
							prefix="sales_by_item"
							data={data}
							columns={columns}
							table={table}
						/>
					</Box>
				)}
			/>
		</LocalizationProvider>
	);
};

export default SalesItemDaily;